import { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryIcon from "@mui/icons-material/Category";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import { djangoApi } from "../../utils/axios/axios.utils";

const MODEL_LABELS = {
    aneis: "Anéis",
    corrente: "Correntes",
    tornozeleira: "Tornozeleiras",
    pulseira: "Pulseiras",
    embalagem: "Embalagens",
    brinco: "Brincos",
    bracelete: "Braceletes",
    conjunto: "Conjuntos",
};

const formatBRL = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
};

const SellerSoldProductsAccordion = ({
    seller,
    theme,
    colors,
    subtleBorder,
    softText,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState("");

    const totalProducts = useMemo(
        () => (Array.isArray(products) ? products.length : 0),
        [products]
    );

    const groupedProducts = useMemo(() => {
        const groups = {};

        (products || []).forEach((item) => {
            const key = item?.content_type__model || "outros";
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });

        return Object.entries(groups).sort((a, b) => {
            const labelA = MODEL_LABELS[a[0]] || a[0];
            const labelB = MODEL_LABELS[b[0]] || b[0];
            return labelA.localeCompare(labelB, "pt-BR");
        });
    }, [products]);

    const fetchProducts = async () => {
        try {
            setIsLoading(true);
            setError("");

            const res = await djangoApi.get(
                `products/register_prods/${seller.id}/get_sold_prods_by_seller/`,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = Array.isArray(res?.data) ? res.data : [];
            setProducts(data);
            setHasLoadedOnce(true);
        } catch (err) {
            console.error("Erro ao buscar peças vendidas da vendedora:", err);
            setError("Não foi possível carregar as peças vendidas.");
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = async (_, isExpanded) => {
        setExpanded(isExpanded);

        if (!isExpanded) return;
        if (!seller?.id) return;
        if (hasLoadedOnce) return;

        await fetchProducts();
    };

    useEffect(() => {
        setExpanded(false);
        setHasLoadedOnce(false);
        setProducts([]);
        setError("");
        setIsLoading(false);
    }, [seller?.id]);

    return (
        <Paper
            elevation={8}
            sx={{
                backgroundColor: colors?.pink?.[900],
                borderRadius: 2,
                border: subtleBorder,
            }}
        >
            <Accordion
                expanded={expanded}
                onChange={handleChange}
                disableGutters
                sx={{
                    overflow: "hidden",
                    border: subtleBorder,
                    backgroundColor: colors?.pink?.[900],
                    bgcolor: "transparent",
                    color: "inherit",
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        px: 2,
                        py: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            pr: 1,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                            <CategoryIcon />
                            <Box>
                                <Typography fontWeight={900}>
                                    Peças vendidas
                                </Typography>
                                <Typography variant="body2" color={softText}>
                                    Catálogo de peças vendidas da vendedora
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {hasLoadedOnce && !isLoading && (
                                <Chip
                                    size="small"
                                    sx={{padding: '10px'}}
                                    icon={<Inventory2OutlinedIcon />}
                                    label={`${totalProducts} peça${totalProducts === 1 ? "" : "s"}`}
                                    variant="outlined"
                                />
                            )}

                            {!hasLoadedOnce && (
                                <Chip
                                    size="small"
                                    label="Carrega ao abrir"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0.5 }}>
                    {!seller?.id && (
                        <Alert severity="info">
                            Selecione uma vendedora para visualizar as peças vendidas.
                        </Alert>
                    )}

                    {!!seller?.id && isLoading && (
                        <Box
                            sx={{
                                minHeight: 180,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <CircularProgress size={28} color="success"/>
                            <Typography variant="body2" color={softText}>
                                Carregando peças vendidas...
                            </Typography>
                        </Box>
                    )}

                    {!!seller?.id && !isLoading && !!error && (
                        <Alert severity="error">{error}</Alert>
                    )}

                    {!!seller?.id && !isLoading && !error && hasLoadedOnce && totalProducts === 0 && (
                        <Alert severity="warning">
                            Nenhuma peça vendida foi encontrada para esta vendedora.
                        </Alert>
                    )}

                    {!!seller?.id && !isLoading && !error && hasLoadedOnce && totalProducts > 0 && (
                        <Box
                            sx={{
                                maxHeight: "70vh",
                                overflowY: "auto",
                                pr: 0.5,
                            }}
                        >
                            {groupedProducts.map(([model, items]) => {
                                const title = MODEL_LABELS[model] || model;

                                return (
                                    <Box key={model} sx={{ mb: 2.5 }}>
                                        <Box
                                            sx={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 3,
                                                py: 1,
                                                px: 1.25,
                                                mb: 1.25,
                                                borderRadius: 0,
                                                border: subtleBorder,
                                                bgcolor:
                                                    theme.palette.mode === "dark"
                                                        ? colors.pink[900]
                                                        : "#fff",
                                                backdropFilter: "blur(8px)",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Typography fontWeight={900}>
                                                    {title}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    label={`${items.length} item${items.length === 1 ? "" : "s"}`}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Box>

                                        <Grid container spacing={1.5}>
                                            {items.map((item) => {
                                                const code = item?.product_id_produto || "-";
                                                const imageUrl = item?.imageUrl;
                                                const size = item?.product_tamanho;
                                                const price = item?.sell_price;
                                                const soldQty = item?.quantity_sold;
                                                const kitNumber = item?.kit__kit_number;

                                                return (
                                                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={item.pk}>
                                                        <Paper
                                                            elevation={2}
                                                            sx={{
                                                                height: "100%",
                                                                borderRadius: 3,
                                                                overflow: "hidden",
                                                                border: subtleBorder,
                                                                bgcolor:
                                                                    theme.palette.mode === "dark"
                                                                        ? colors.pink[800]
                                                                        : "#fff",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: "100%",
                                                                    aspectRatio: "1 / 1",
                                                                    bgcolor:
                                                                        theme.palette.mode === "dark"
                                                                            ? colors.pink[900]
                                                                            : "#f7f7f7",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    overflow: "hidden",
                                                                }}
                                                            >
                                                                {imageUrl ? (
                                                                    <Box
                                                                        component="img"
                                                                        src={imageUrl}
                                                                        alt={String(code)}
                                                                        sx={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body2" color={softText}>
                                                                        Sem imagem
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            <Box sx={{ p: 1.25 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={900}
                                                                    sx={{
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {code}
                                                                </Typography>

                                                                <Typography variant="caption" color={softText} display="block">
                                                                    Kit {kitNumber || "-"}
                                                                </Typography>

                                                                {!!size && (
                                                                    <Typography variant="caption" color={softText} display="block">
                                                                        Tam. {size}
                                                                    </Typography>
                                                                )}

                                                                <Typography
                                                                    variant="caption"
                                                                    color={softText}
                                                                    display="block"
                                                                    sx={{ mt: 0.5 }}
                                                                >
                                                                    Vendidos: {soldQty || 0}
                                                                </Typography>

                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={800}
                                                                    sx={{ mt: 0.75 }}
                                                                >
                                                                    {formatBRL(price)}
                                                                </Typography>
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
};

export default SellerSoldProductsAccordion;